import { useEffect, useState } from "react";
import { toast } from "sonner";

import { BrandModal } from "@/components/common/BrandModal";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { useProfileItems } from "@/hooks/queries/useProfileItems";
import { useUpdateProfileItems } from "@/hooks/mutations/useProfileItemMutations";
import { TermChip } from "./TermChip";
import type { ProfileItem, SearchProfile } from "@/types/domain";

export function KeywordsDialog({
  profile,
  open,
  onOpenChange,
}: {
  profile: SearchProfile;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { data: existing = [] } = useProfileItems(profile.searchprofile_id);
  const update = useUpdateProfileItems();
  const [items, setItems] = useState<ProfileItem[]>([]);
  const [keyword, setKeyword] = useState("");

  useEffect(() => {
    if (open) setItems(existing);
  }, [open, profile.searchprofile_id]);

  function addItem() {
    // Se guarda tal cual: el espacio al inicio/final es intencional
    // (ej. "NiFF " no debe matchear "NIFFX").
    if (!keyword.trim() || items.some((item) => item.keyword === keyword))
      return;
    setItems((prev) => [...prev, { keyword, active: true }]);
    setKeyword("");
  }

  function renameItem(index: number, next: string) {
    if (items.some((item) => item.keyword === next)) return;
    setItems((p) =>
      p.map((item, i) => (i === index ? { ...item, keyword: next } : item)),
    );
  }

  function toggleItem(index: number) {
    setItems((p) =>
      p.map((item, i) =>
        i === index ? { ...item, active: !(item.active ?? true) } : item,
      ),
    );
  }

  function save() {
    update.mutate(
      { items, searchProfileId: profile.searchprofile_id },
      {
        onSuccess: () => {
          toast.success("Items actualizados");
          onOpenChange(false);
        },
        onError: () => toast.error("No se pudieron guardar los items"),
      },
    );
  }

  return (
    <BrandModal
      open={open}
      onOpenChange={onOpenChange}
      title="Gestionar items"
      description={
        <>
          Define los items que deseas rastrear en las licitaciones de bienes y
          servicios (ej. "limpieza", "seguridad", "computadoras"). El sistema
          marcará los procesos cuyos ítems coincidan con alguno de estos
          términos.
        </>
      }
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button onClick={save} disabled={update.isPending}>
            {update.isPending ? "Guardando..." : "Guardar cambios"}
          </Button>
        </>
      }
    >
      <div className="space-y-1.5">
        <Label className="text-sm font-semibold">Item*</Label>
        <div className="flex gap-3">
          <Input
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && addItem()}
            className="flex-1"
          />
          <Button
            variant="secondary"
            onClick={addItem}
            disabled={!keyword.trim()}
          >
            + Agregar
          </Button>
        </div>
      </div>

      <Separator />

      <div className="space-y-2">
        <p className="text-sm font-bold">Tus items:</p>
        {items.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No has agregado ningún item todavía. Usa el formulario de arriba para
            agregarlos.
          </p>
        ) : (
          <div className="flex flex-wrap gap-1.5">
            {items.map((item, i) => (
              <TermChip
                key={i}
                term={item.keyword}
                active={item.active ?? true}
                onRemove={() => setItems((p) => p.filter((_, idx) => idx !== i))}
                onRename={(next) => renameItem(i, next)}
                onToggle={() => toggleItem(i)}
              />
            ))}
          </div>
        )}
      </div>
    </BrandModal>
  );
}
